import { CheckIcon } from '@heroicons/react/20/solid';
import CT from '@marketplace-ai/tags';
import { useSelector } from '@xstate/react';
import { useEffect, useState } from 'react';
import { LoadingSpinner } from '~/components';
import { CostCalculatorFailureNotice } from '~/features/market/listings/components/CostCalculatorFailureNotice';
import {
  formatCalculatedMoney,
  hasValidCostCalculation,
} from '~/features/market/listings/components/costCalculatorMoney';
import { jobListingFeeNote } from '~/features/market/listings/components/feeDisclosure';
import { formatSubspecialties } from '~/features/market/listings/subspecialties';
import { dateStringFormatter } from '~/features/utils';
import { loadCategoryOptions } from '~/features/vault/profile/api';
import { InfoSection } from '../../../components/InfoSection';
import { useJobListingActorRef } from '../../../job-listing/jobListingProvider';
import { calculateLocalDifferentialTotals, formatShiftDistributionStr } from './util';

const TAGS = CT.employer.jobListings.reviewInfo;

export const ReviewInfo = ({ footer }: { footer: React.ReactNode }) => {
  const jobListingRef = useJobListingActorRef();
  const jobListing = useSelector(jobListingRef, (state) => state.context.listing);
  const summary = useSelector(jobListingRef, (state) => state.context.summary);
  const [providerCategory, setProviderCategory] = useState<string>('');

  useEffect(() => {
    const getProviderCategory = async () => {
      const options = await loadCategoryOptions();
      if (!jobListing) return;
      const providerCategory = options.find(
        (option) => option.value === jobListing.providerCategoryId
      );
      if (!providerCategory) return;
      setProviderCategory(providerCategory.label);
    };
    getProviderCategory();
  }, [jobListing]);

  if (!jobListing?.detail || !providerCategory) {
    return <LoadingSpinner />;
  }
  const hasValidCalculation = hasValidCostCalculation(jobListing.detail);
  const localDifferentials = calculateLocalDifferentialTotals(jobListing.detail);
  const subspecialties = formatSubspecialties(jobListing);
  // [MCV-1949] Fee disclosure for the listing's type; explicit text-left because #root centers
  // text. A listing whose calculation failed can still be submitted, so it shows there too.
  const feeNote = (
    <p
      className="mt-3 text-left text-[13px] leading-snug text-grey-300"
      data-cy={TAGS.feeDisclaimer}
    >
      {jobListingFeeNote(jobListing.jobType, jobListing.providerCategoryId)}
    </p>
  );
  return (
    <div className="mt-8">
      <InfoSection
        title="Basic Info"
        infoList={[
          { label: 'Title', value: jobListing.jobTitle, cyTag: TAGS.title },
          { label: 'Provider Category', value: providerCategory, cyTag: TAGS.providerCategory },
          {
            label: 'Specialty',
            value: jobListing.specialtyName || 'Not Provided',
            cyTag: TAGS.specialty,
          },
          ...(subspecialties
            ? [{ label: 'Subspecialties', value: subspecialties, cyTag: TAGS.subspecialties }]
            : []),
        ]}
      />
      <InfoSection
        title="Labor Metrics"
        infoList={[
          {
            label: 'Contract Period',
            value: `${dateStringFormatter(jobListing.detail.startDate)} - ${
              jobListing.detail.endDate
                ? dateStringFormatter(jobListing.detail.endDate)
                : 'Not Provided'
            }`,
            cyTag: TAGS.startDate,
          },

          {
            label: 'Total Shifts',
            value: jobListing.detail.numOfShifts?.toString() ?? 'Not Provided',
            cyTag: TAGS.totalShifts,
          },
          {
            label: 'Total Hours',
            value:
              jobListing.detail.hoursPerShift !== null && jobListing.detail.numOfShifts !== null
                ? `${jobListing.detail.hoursPerShift * jobListing.detail.numOfShifts}`
                : 'Not Provided',
            cyTag: TAGS.totalHours,
          },
        ]}
      />
      <InfoSection
        title="Shift Distribution & Differentials"
        infoList={[
          {
            label: 'Weekday Shifts',
            value: formatShiftDistributionStr(
              jobListing.detail.numDayShifts,
              jobListing.detail.numEveShifts,
              jobListing.detail.numNightShifts
            ),
            cyTag: TAGS.weekdayShifts,
          },
          {
            label: 'Weekend Shifts',
            value: formatShiftDistributionStr(
              jobListing.detail.numOfWeDayShifts,
              jobListing.detail.numOfWeEveShifts,
              jobListing.detail.numOfWeNightShifts
            ),
            cyTag: TAGS.weekendShifts,
          },
          {
            label: 'Holiday Shifts',
            value: formatShiftDistributionStr(
              jobListing.detail.numHolDayShifts,
              jobListing.detail.numHolEveShifts,
              jobListing.detail.numHolNightShifts
            ),
            cyTag: TAGS.holidayShifts,
          },
          {
            label: 'Total Differentials',
            value: formatCalculatedMoney(
              summary?.shiftDifferentialTotal ?? localDifferentials.shift
            ),
            cyTag: TAGS.diffCost,
          },
        ]}
      />
      <InfoSection
        title="Call Shift Distribution & Differentials"
        infoList={[
          {
            label: 'On-Call Weekday Shifts',
            value: formatShiftDistributionStr(
              jobListing.detail.numDayCallShifts,
              jobListing.detail.numEveCallShifts,
              jobListing.detail.numNightCallShifts
            ),
            cyTag: TAGS.weekDayCallShifts,
          },
          {
            label: 'Weekend Shifts',
            value: formatShiftDistributionStr(
              jobListing.detail.numOfWeDayCallShifts,
              jobListing.detail.numOfWeEveCallShifts,
              jobListing.detail.numOfWeNightCallShifts
            ),
            cyTag: TAGS.weekEndCallShift,
          },
          {
            label: 'Holiday Shifts',
            value: formatShiftDistributionStr(
              jobListing.detail.numHolDayCallShifts,
              jobListing.detail.numHolEveCallShifts,
              jobListing.detail.numHolNightCallShifts
            ),
            cyTag: TAGS.holidayCallShifts,
          },
          {
            label: 'Total Differentials',
            value: formatCalculatedMoney(
              summary?.callShiftDifferentialTotal ?? localDifferentials.call
            ),
            cyTag: TAGS.callDiffCost,
          },
        ]}
      />
      <InfoSection
        title="Compensation Summary"
        infoList={[
          {
            label: 'Compensation Type',
            value:
              jobListing.detail.compType != null
                ? jobListing.detail.compType.charAt(0) +
                  jobListing.detail.compType.slice(1).toLowerCase()
                : 'Not Provided',
            cyTag: TAGS.compType,
          },
          {
            label: 'Average Base Hourly Rate',
            value: formatCalculatedMoney(jobListing.detail.pricePerHour),
            cyTag: TAGS.avgBaseHourlyRate,
          },
          {
            label: 'Average Hourly Rate with Differentials',
            value: formatCalculatedMoney(jobListing.detail.avgHourlyRate),
            cyTag: TAGS.avgRateWithDiff,
          },
          {
            label: 'Total Contract Value',
            value: formatCalculatedMoney(jobListing.detail.totalContractValue),
            cyTag: TAGS.totalContractValue,
          },
        ]}
      />

      {hasValidCalculation ? (
        <div className="w-full bg-success-subtle p-4 rounded-lg mb-4 mt-3">
          <div className="flex items-center mb-3">
            <CheckIcon className="h-5 w-5 text-success stroke-4 mr-3" />
            <p className="body-text-sb">Ready to Submit</p>
          </div>
          <p className="body-text-sb">
            Your Job Listing is complete and ready to be published. Once submitted, it will be
            visible to qualified providers on the platform.
          </p>
          {feeNote}
        </div>
      ) : (
        <div className="mb-4 mt-3">
          <CostCalculatorFailureNotice />
          {feeNote}
        </div>
      )}
      {footer}
    </div>
  );
};
